import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { ListSkeleton } from "@/components/skeletons";
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  Play,
  Pause,
  DollarSign,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Settings2,
  WifiOff,
} from "lucide-react";

// ─── Types ───

interface AdSetEntity {
  adSetId: string;
  adSetName: string;
  campaignId: string;
  dailyBudget: number | null;
  lifetimeBudget: number | null;
  status: string;
}

interface CampaignEntity {
  campaignId: string;
  campaignName: string;
  dailyBudget: number | null;
  lifetimeBudget: number | null;
  status: string;
  adSets: AdSetEntity[];
}

interface AdSetActionsPanelProps {
  forUserId?: string;
  clientAdAccountIds?: string[];
}

// ─── Helpers ───

function formatBudget(
  daily: number | null,
  lifetime: number | null,
  t: (k: string, opts?: Record<string, unknown>) => string,
  currencySuffix: string
): string {
  if (daily != null)
    return t("adsetActions.budgetPerDay", { amount: daily.toFixed(2), currency: currencySuffix });
  if (lifetime != null)
    return t("adsetActions.budgetLifetime", {
      amount: lifetime.toFixed(2),
      currency: currencySuffix,
    });
  return t("adsetActions.cbo");
}

// ─── Inline Budget Editor ───

function BudgetEditor({
  entityId,
  entityName,
  entityType,
  currentDailyBudget,
  currentLifetimeBudget,
  forUserId,
  onDone,
}: {
  entityId: string;
  entityName: string;
  entityType: "campaign" | "adset";
  currentDailyBudget: number | null;
  currentLifetimeBudget: number | null;
  forUserId?: string;
  onDone: () => void;
}) {
  const { t, i18n } = useTranslation();
  const currencySuffix = i18n.language === "pl" ? "PLN" : "$";
  const isLifetime = currentLifetimeBudget != null && currentDailyBudget == null;
  const currentValue = isLifetime ? currentLifetimeBudget : currentDailyBudget;
  const [value, setValue] = useState(currentValue?.toString() || "");
  const [confirmed, setConfirmed] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      const numVal = parseFloat(value);
      if (isNaN(numVal) || numVal <= 0) throw new Error(t("adsetActions.invalidAmount"));
      return apiRequest("POST", "/api/marketing-analyst/apply-action", {
        action: "update_budget",
        entityType,
        entityId,
        entityName,
        ...(isLifetime ? { newLifetimeBudget: numVal } : { newDailyBudget: numVal }),
        description: t("adsetActions.budgetChangeDesc", {
          amount: numVal,
          currency: currencySuffix,
        }),
        forUserId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["analyst-entities"] });
      onDone();
    },
  });

  if (mutation.isSuccess) {
    return (
      <div className="flex items-center gap-2 text-xs text-emerald-400">
        <CheckCircle className="w-3.5 h-3.5" />
        <span>{t("adsetActions.budgetChanged", { value, currency: currencySuffix })}</span>
      </div>
    );
  }

  if (!confirmed) {
    return (
      <div className="flex items-center gap-2">
        <input
          type="number"
          step="1"
          min="1"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-24 px-2 py-1 text-xs bg-[#0a0c10] border border-white/[0.15] rounded text-white focus:outline-none focus:border-amber-500/50"
          placeholder={currencySuffix}
        />
        <button
          onClick={() => setConfirmed(true)}
          disabled={!value || parseFloat(value) <= 0}
          className="px-2 py-1 text-xs rounded bg-amber-600/30 text-amber-300 hover:bg-amber-600/50 border border-amber-500/40 transition-colors disabled:opacity-40"
        >
          {t("adsetActions.change")}
        </button>
        <button onClick={onDone} className="px-2 py-1 text-xs text-white/50 hover:text-white/70">
          ✕
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-2 py-1 rounded border border-amber-600/40 bg-amber-950/30">
      <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
      <span className="text-xs text-amber-200 flex-1">
        {isLifetime
          ? t("adsetActions.confirmBudgetLifetime", { value, currency: currencySuffix })
          : t("adsetActions.confirmBudgetDaily", { value, currency: currencySuffix })}
      </span>
      <button
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-600/30 text-emerald-300 hover:bg-emerald-600/50 border border-emerald-500/40 disabled:opacity-50"
      >
        {mutation.isPending ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <CheckCircle className="w-3 h-3" />
        )}
        {t("adsetActions.yes")}
      </button>
      <button
        onClick={() => setConfirmed(false)}
        className="px-2 py-0.5 rounded text-xs text-white/60 hover:text-white/80"
      >
        {t("adsetActions.no")}
      </button>
      {mutation.isError && (
        <span className="text-xs text-red-400">{(mutation.error as Error).message}</span>
      )}
    </div>
  );
}

// ─── Status Toggle Button ───

function StatusToggle({
  entityId,
  entityName,
  entityType,
  currentStatus,
  forUserId,
}: {
  entityId: string;
  entityName: string;
  entityType: "campaign" | "adset";
  currentStatus: string;
  forUserId?: string;
}) {
  const { t } = useTranslation();
  const [confirmed, setConfirmed] = useState(false);

  const isActive = currentStatus === "ACTIVE";
  const newStatus = isActive ? "PAUSED" : "ACTIVE";

  const mutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/marketing-analyst/apply-action", {
        action: "update_status",
        entityType,
        entityId,
        entityName,
        newStatus,
        description:
          newStatus === "PAUSED"
            ? t("adsetActions.pauseDesc", { name: entityName })
            : t("adsetActions.resumeDesc", { name: entityName }),
        forUserId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["analyst-entities"] });
      setConfirmed(false);
    },
  });

  if (mutation.isSuccess) {
    return (
      <div className="flex items-center gap-1 text-xs text-emerald-400">
        <CheckCircle className="w-3 h-3" />
        <span>{newStatus === "PAUSED" ? t("adsetActions.paused") : t("adsetActions.resumed")}</span>
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="flex items-center gap-2 px-2 py-1 rounded border border-amber-600/40 bg-amber-950/30">
        <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
        <span className="text-xs text-amber-200">
          {newStatus === "PAUSED"
            ? t("adsetActions.confirmPause", { name: entityName })
            : t("adsetActions.confirmResume", { name: entityName })}
        </span>
        <button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-600/30 text-emerald-300 hover:bg-emerald-600/50 border border-emerald-500/40 disabled:opacity-50"
        >
          {mutation.isPending ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <CheckCircle className="w-3 h-3" />
          )}
          {t("adsetActions.yes")}
        </button>
        <button
          onClick={() => setConfirmed(false)}
          className="px-2 py-0.5 rounded text-xs text-white/60 hover:text-white/80"
        >
          {t("adsetActions.no")}
        </button>
        {mutation.isError && (
          <span className="text-xs text-red-400">{(mutation.error as Error).message}</span>
        )}
      </div>
    );
  }

  return (
    <button
      onClick={() => setConfirmed(true)}
      className={`flex items-center gap-1 px-2 py-1 rounded text-xs border transition-colors ${
        isActive
          ? "border-orange-400/50 bg-orange-50 text-orange-700 hover:bg-orange-100 dark:border-orange-600/40 dark:bg-orange-950/30 dark:text-orange-300 dark:hover:bg-orange-950/50"
          : "border-green-400/50 bg-green-50 text-green-700 hover:bg-green-100 dark:border-green-600/40 dark:bg-green-950/30 dark:text-green-300 dark:hover:bg-green-950/50"
      }`}
      title={isActive ? t("adsetActions.pause") : t("adsetActions.resume")}
    >
      {isActive ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
      <span>{isActive ? t("adsetActions.pause") : t("adsetActions.resume")}</span>
    </button>
  );
}

// ─── Ad Set Row ───

function AdSetRow({ adSet, forUserId }: { adSet: AdSetEntity; forUserId?: string }) {
  const { t, i18n } = useTranslation();
  const currencySuffix = i18n.language === "pl" ? "PLN" : "$";
  const [editingBudget, setEditingBudget] = useState(false);

  return (
    <div className="flex flex-col gap-1.5 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.1] transition-colors">
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-white/90 truncate">{adSet.adSetName}</div>
          <div className="text-[10px] text-white/40 mt-0.5">
            {formatBudget(adSet.dailyBudget, adSet.lifetimeBudget, t, currencySuffix)}
            <span
              className={`ml-2 ${adSet.status === "ACTIVE" ? "text-green-400" : "text-orange-400"}`}
            >
              {adSet.status === "ACTIVE" ? t("adsetActions.active") : t("adsetActions.inactive")}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => setEditingBudget(!editingBudget)}
            className="flex items-center gap-1 px-2 py-1 rounded text-xs border border-blue-400/50 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-600/40 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50 transition-colors"
            title={t("adsetActions.changeBudget")}
          >
            <DollarSign className="w-3 h-3" />
            <span>{t("adsetActions.budget")}</span>
          </button>
          <StatusToggle
            entityId={adSet.adSetId}
            entityName={adSet.adSetName}
            entityType="adset"
            currentStatus={adSet.status}
            forUserId={forUserId}
          />
        </div>
      </div>
      {editingBudget && (
        <BudgetEditor
          entityId={adSet.adSetId}
          entityName={adSet.adSetName}
          entityType="adset"
          currentDailyBudget={adSet.dailyBudget}
          currentLifetimeBudget={adSet.lifetimeBudget}
          forUserId={forUserId}
          onDone={() => setEditingBudget(false)}
        />
      )}
    </div>
  );
}

// ─── Campaign Section ───

function CampaignSection({
  campaign,
  forUserId,
}: {
  campaign: CampaignEntity;
  forUserId?: string;
}) {
  const { t, i18n } = useTranslation();
  const currencySuffix = i18n.language === "pl" ? "PLN" : "$";
  const [expanded, setExpanded] = useState(true);

  if (campaign.adSets.length === 0) return null;

  return (
    <div className="border border-white/[0.08] rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-white/[0.03] hover:bg-white/[0.05] transition-colors text-left"
      >
        {expanded ? (
          <ChevronDown className="w-3.5 h-3.5 text-white/50" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 text-white/50" />
        )}
        <div className="flex-1 min-w-0">
          <span className="text-xs font-semibold text-white/90 truncate block">
            {campaign.campaignName}
          </span>
          {campaign.dailyBudget != null && (
            <span className="text-[10px] text-white/40">
              {t("adsetActions.cboBudget", {
                amount: campaign.dailyBudget.toFixed(2),
                currency: currencySuffix,
              })}
            </span>
          )}
        </div>
        <span className="text-[10px] text-white/40">
          {t("adsetActions.adSetsCount", { count: campaign.adSets.length })}
        </span>
      </button>
      {expanded && (
        <div className="p-2 space-y-1.5">
          {campaign.adSets.map((adSet) => (
            <AdSetRow key={adSet.adSetId} adSet={adSet} forUserId={forUserId} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Panel ───

export default function AdSetActionsPanel({
  forUserId,
  clientAdAccountIds,
}: AdSetActionsPanelProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery<{ campaigns: CampaignEntity[] }>({
    queryKey: ["analyst-entities", forUserId, clientAdAccountIds],
    queryFn: () =>
      apiRequest("POST", "/api/marketing-analyst/entities", {
        forUserId,
        clientAdAccountIds,
      }),
    enabled: visible,
    staleTime: 60_000,
  });

  const campaigns = data?.campaigns?.filter((c) => c.adSets.length > 0) || [];

  return (
    <div className="space-y-3">
      <button
        onClick={() => setVisible(!visible)}
        className={`w-full group flex items-center justify-between rounded-xl border transition-all duration-200 px-4 py-4 ${
          visible
            ? "border-white/[0.12] bg-white/[0.04]"
            : "border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/[0.12]"
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/[0.06] border border-white/[0.08]">
            <Settings2 className="w-5 h-5 text-white/60" />
          </div>
          <div className="text-left">
            <span className="text-sm font-semibold text-white block">
              {t("adsetActions.manageTitle")}
            </span>
            <span className="text-xs text-white/40">{t("adsetActions.manageSubtitle")}</span>
          </div>
        </div>
        {visible ? (
          <ChevronDown className="w-4 h-4 text-white/40" />
        ) : (
          <ChevronRight className="w-4 h-4 text-white/30" />
        )}
      </button>

      {visible && (
        <div className="bg-white/[0.02] border border-white/[0.08] rounded-xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-white/[0.06]">
            <span className="text-xs font-semibold text-white/70">
              {t("adsetActions.activeHeader")}
            </span>
            <button
              onClick={() => refetch()}
              disabled={isLoading}
              className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-white/50 hover:text-white/70 hover:bg-white/[0.04] transition-colors"
            >
              <RefreshCw className={`w-3 h-3 ${isLoading ? "animate-spin" : ""}`} />
              {t("adsetActions.refresh")}
            </button>
          </div>

          {/* Content */}
          <div className="p-3 space-y-2 max-h-[500px] overflow-y-auto custom-scrollbar">
            {/* VP-208: lista kampanii/ad-setów ładuje się w panelu o stałej wysokości —
                placeholder wierszy zamiast wyśrodkowanego spinnera. */}
            {isLoading && <ListSkeleton rows={4} withAvatar={false} />}

            {isError && (
              <div className="px-3 py-4 space-y-3">
                {(error as Error)?.message?.toLowerCase().includes("no meta ads connection") ||
                (error as Error)?.message?.toLowerCase().includes("connect the account") ? (
                  <div className="flex flex-col items-center gap-2 text-center py-2">
                    <WifiOff className="w-6 h-6 text-white/30" />
                    <span className="text-xs font-semibold text-white/70">
                      {t("adsetActions.noConnectionTitle")}
                    </span>
                    <span className="text-xs text-white/40">
                      {t("adsetActions.noConnectionDesc")}
                    </span>
                    <a
                      href="/settings?tab=integrations"
                      className="mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-medium hover:bg-blue-500/20 transition-colors"
                    >
                      <Settings2 className="w-3 h-3" />
                      {t("adsetActions.reconnect")}
                    </a>
                  </div>
                ) : (
                  <div className="text-xs text-red-400 px-2 py-1">
                    {(error as Error)?.message || t("adsetActions.fetchError")}
                  </div>
                )}
              </div>
            )}

            {!isLoading && !isError && campaigns.length === 0 && (
              <div className="text-xs text-white/40 text-center py-4">
                {t("adsetActions.noCampaigns")}
              </div>
            )}

            {campaigns.map((campaign) => (
              <CampaignSection
                key={campaign.campaignId || campaign.adSets[0]?.adSetId}
                campaign={campaign}
                forUserId={forUserId}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
